<script lang="ts">
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {RecordSearchParams} from 'typings/portaro.be.types';
    import type {LogbookReportPairingType} from './types';
    import type {SearchManager} from 'src/features/search/search-manager/search-manager';
    import {getProjectLogbookPairingContext} from './project-logbook-pairing-context';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {Kind, SearchType} from 'shared/constants/portaro.constants';
    import {getDateFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {hasOriginRecordReference} from 'src/features/record-grid/lib/types-utils';
    import {fade} from 'svelte/transition';
    import {mapFieldTypeIdToFilterField} from 'src/features/search/kp-search-field-type-filter';
    import {FOND_INSTALLATION_LOGBOOK, FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';
    import {logbookReportsPageSize} from './types';
    import {SUTOR_REPORT_TYPES} from 'src/features/erp-sutor/sutor-constants';
    import KpBarebonesTable from 'shared/ui-widgets/table/barebones/KpBarebonesTable.svelte';
    import KpPageableSearchResults from 'src/features/search/kp-search-results/KpPageableSearchResults.svelte';
    import KpSearchContext from 'src/features/search/kp-search-context/KpSearchContext.svelte';
    import KpSearchToolbar from 'src/features/search/kp-search-toolbar/KpSearchToolbar.svelte';
    import LogbookReportItem from './LogbookReportItem.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';
    import TableRecordLinkHeader from 'shared/ui-widgets/table/barebones/TableRecordLinkHeader.svelte';

    export let logbookRecord: RecordRow;
    export let pairingType: LogbookReportPairingType;

    export let getSearchManager: () => SearchManager<RecordRow>; // Bind outside

    const context = getProjectLogbookPairingContext();

    const logbookDateField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.dateFieldId);
    const logbookCustomerProjectField = getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.customerProjectFieldId);
    const logbookDate = getDateFromGridFieldValue(logbookDateField);
    const processedReports$ = context.processedReports$;

    function createStaticParams(): RecordSearchParams {
        return {
            kind: [Kind.KIND_RECORD],
            type: SearchType.TYPE_TABLE,
            rootFond: [{id: FOND_REPORT.fond}],
            fond: FOND_REPORT.subfondsForLogbookPairing.map((fondId) => ({id: fondId})),
            pageSize: logbookReportsPageSize,
            qt: JSON.stringify(pairingType === 'unpair' ? createPairedConjunction() : createUnpairedConjunction())
        };
    }

    function createPairedConjunction() {
        return {
            and: [
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                    eq: {
                        value: logbookRecord.id
                    }
                }
            ]
        };
    }

    function createUnpairedConjunction() {
        let customerProjectId = context.projectRecord.id;

        if (hasOriginRecordReference(logbookCustomerProjectField)) {
            customerProjectId = logbookCustomerProjectField.originRecordReference.id;
        }

        return {
            and: [
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.dateFieldId, 'val'),
                    eq: {
                        value: logbookDate.toISODate()
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.projectFieldId, 'link'),
                    eq: {
                        value: context.projectRecord.id
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.customerProjectMainFieldId, 'link'),
                    eq: {
                        value: customerProjectId
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.WORK.typeFieldId, 'val'),
                    eq: {
                        value: SUTOR_REPORT_TYPES.WORK
                    }
                },
                {
                    field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                    present: {
                        value: false
                    }
                },
                {
                    not: {
                        field: mapFieldTypeIdToFilterField(FOND_REPORT.installationLogbookFieldId, 'link'),
                        eq: {
                            value: logbookRecord.id
                        }
                    }
                }
            ]
        };
    }
</script>

<Flex direction="column" gap="xxl" fillAvailableSpace>
    <KpSearchContext staticParams={createStaticParams()} localSearch let:searchManager bind:getSearchManager>
        <KpSearchToolbar/>

        <KpPageableSearchResults pagination={searchManager.getPagination()} let:paginationData>
            <KpBarebonesTable fontSize="12px">
                <tr slot="header">
                    <TableRecordLinkHeader/>

                    {#if pairingType === 'pair'}
                        <th class="button-header"></th>
                    {/if}

                    <th>Pracovník/Stroj</th>
                    <th>Datum</th>
                    <th>Vykázáno</th>
                    <th>Účtováno</th>
                    <th>Typ</th>

                    {#if pairingType === 'pair'}
                        <th>Stav</th>
                    {/if}

                    {#if pairingType === 'unpair'}
                        <th class="button-header"></th>
                    {/if}
                </tr>

                <svelte:fragment slot="body">
                    {#each paginationData.items as record(record.id)}
                        <tr transition:fade={{duration: 250}}>
                            <LogbookReportItem {record}
                                               {pairingType}
                                               isProcessing={$processedReports$.includes(record.id)} on:pair-unpair/>
                        </tr>
                    {/each}
                </svelte:fragment>
            </KpBarebonesTable>
        </KpPageableSearchResults>
    </KpSearchContext>
</Flex>

<style lang="less">
    .button-header {
        width: 70px;
    }
</style>